import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "topicsList",
    "topicTemplate",
    "keywordTemplate",
    "topicName",
    "keywordInput",
    "promptsContainer",
    "promptInput",
    "keywordsContainer",
  ];
  static values = {
    nextIndex: Number,
    usedColors: Array,
    availableColors: Array,
    showActiveToggle: Boolean,
  };

  connect() {
    this.topicCounter = {};
    this.keywordCounters = {};
    // Track colors used in the current session
    this.sessionUsedColors = new Set(this.usedColorsValue || []);
    this.colors = this.availableColorsValue || [
      "bg-pink-500",
      "bg-yellow-500",
      "bg-blue-500",
      "bg-green-500",
      "bg-purple-500",
      "bg-red-500",
      "bg-indigo-500",
    ];

    // Track colors from existing topics in the DOM
    this.updateUsedColorsFromDOM();
    this.initializeKeywordCountersFromDOM();
  }

  updateUsedColorsFromDOM() {
    if (!this.hasTopicsListTarget) return;
    const colorIndicators =
      this.topicsListTarget.querySelectorAll("[data-topic-color]");
    colorIndicators.forEach((indicator) => {
      const color = indicator.getAttribute("data-topic-color");
      if (color) {
        this.sessionUsedColors.add(color);
      }
    });
  }

  initializeKeywordCountersFromDOM() {
    if (!this.hasTopicsListTarget) return;

    const topicRows = this.topicsListTarget.querySelectorAll("[data-topic-row]");
    topicRows.forEach((topicRow) => {
      const addKeywordButton = topicRow.querySelector(
        '[data-action*="addKeyword"]'
      );
      const topicName = addKeywordButton?.getAttribute("data-topic-name");
      if (!topicName) return;

      const keywordInputs = topicRow.querySelectorAll(
        '[data-wizard-topics-target="keywordInput"]'
      );
      let maxIndex = -1;
      keywordInputs.forEach((input) => {
        const index = this.extractKeywordIndex(input.name);
        if (index !== null && index > maxIndex) {
          maxIndex = index;
        }
      });

      this.keywordCounters[topicName] = maxIndex + 1;
    });
  }

  extractKeywordIndex(name) {
    if (!name) return null;
    const match = name.match(/\[keywords\]\[(\d+)\]\[keyword\]/);
    return match ? parseInt(match[1], 10) : null;
  }

  getNextAvailableColor() {
    return (
      this.colors.find((color) => !this.sessionUsedColors.has(color)) ||
      this.colors[0]
    );
  }

  addTopic() {
    const topicName = `Topic ${Date.now()}`;
    const nextColor = this.getNextAvailableColor();
    this.sessionUsedColors.add(nextColor);

    const html = this.topicTemplateTarget.innerHTML.replace(
      /__TOPIC_NAME__/g,
      topicName
    );

    this.topicsListTarget.insertAdjacentHTML("beforeend", html);

    // Update color indicator and hidden input
    const newTopic = this.topicsListTarget.lastElementChild;
    const colorIndicator = newTopic.querySelector(".topic-color-indicator");
    const colorInput = newTopic.querySelector(
      '[data-wizard-topics-target="topicColor"]'
    );

    if (colorIndicator) {
      colorIndicator.className = `w-3 h-3 rounded-full ${nextColor} topic-color-indicator`;
      colorIndicator.setAttribute("data-topic-color", nextColor);
    }

    if (colorInput) {
      colorInput.value = nextColor;
    }

    // Focus the topic name input
    const input = newTopic.querySelector(
      '[data-wizard-topics-target="topicName"]'
    );
    if (input) {
      input.select();
      input.focus();
    }

    // Initialize keyword counter for this topic
    this.keywordCounters[topicName] = 0;
  }

  removeTopic(event) {
    event.preventDefault();
    const topicRow = event.target.closest("[data-topic-row]");
    if (topicRow) {
      topicRow.remove();
    }
  }

  addKeyword(event) {
    const topicName = event.currentTarget.getAttribute("data-topic-name");
    const topicRow = event.currentTarget.closest("[data-topic-row]");
    const keywordsContainer = topicRow.querySelector(
      '[data-wizard-topics-target="keywordsContainer"]'
    );

    // Initialize counter if needed
    if (this.keywordCounters[topicName] === undefined) {
      this.keywordCounters[topicName] = 0;
    }
    const keywordIndex = this.keywordCounters[topicName]++;

    if (keywordsContainer) {
      const html = this.keywordTemplateTarget.innerHTML
        .replace(/__TOPIC_NAME__/g, topicName)
        .replace(/__KEYWORD_INDEX__/g, keywordIndex);

      keywordsContainer.insertAdjacentHTML("beforeend", html);

      // Focus the keyword input
      const newKeyword = keywordsContainer.lastElementChild;
      const input = newKeyword.querySelector(
        '[data-wizard-topics-target="keywordInput"]'
      );
      if (input) input.focus();
    }
  }

  removeKeyword(event) {
    event.preventDefault();
    const keywordRow = event.target.closest("[data-keyword-row]");
    if (keywordRow) {
      keywordRow.remove();
    }
  }

  addPrompt(event) {
    const topicName = event.currentTarget.getAttribute("data-topic-name");
    const keywordIndex = event.currentTarget.getAttribute("data-keyword-index");
    const keywordRow = event.currentTarget.closest("[data-keyword-row]");
    const promptsContainer = keywordRow.querySelector(
      '[data-wizard-topics-target="promptsContainer"]'
    );

    // Extract topic name and keyword index from existing inputs if not provided
    let finalTopicName = topicName;
    let finalKeywordIndex = keywordIndex;

    if (!finalTopicName || !finalKeywordIndex) {
      const topicRow = keywordRow.closest("[data-topic-row]");
      const topicNameInput = topicRow.querySelector(
        '[data-wizard-topics-target="topicName"]'
      );
      finalTopicName = topicNameInput
        ? topicNameInput.value
        : topicNameInput.name.match(/\[topics\]\[([^\]]+)\]/)[1];

      const keywordInput = keywordRow.querySelector(
        '[data-wizard-topics-target="keywordInput"]'
      );
      if (keywordInput && keywordInput.name) {
        const match = keywordInput.name.match(/\[keywords\]\[(\d+)\]/);
        finalKeywordIndex = match ? match[1] : keywordIndex;
      }
    }

    if (promptsContainer) {
      const promptRow = document.createElement("div");
      promptRow.className = "flex items-center gap-2";
      promptRow.setAttribute("data-prompt-row", "");

      const activeToggleMarkup = this.showActiveToggleValue
        ? `
          <input 
            type="hidden" 
            name="wizard[topics][${finalTopicName}][keywords][${finalKeywordIndex}][questions_active][]" 
            value="1"
            data-wizard-topics-target="promptActiveInput"
          >
          <label class="flex items-center gap-1 text-xs text-base-content/60">
            <input 
              type="checkbox" 
              class="toggle toggle-xs"
              checked
              data-action="wizard-topics#togglePromptActive"
            >
            <span>Active</span>
          </label>
        `
        : "";

      promptRow.innerHTML = `
        <input 
          type="text" 
          name="wizard[topics][${finalTopicName}][keywords][${finalKeywordIndex}][questions][]" 
          class="input input-bordered input-sm flex-1" 
          placeholder="Enter a prompt or question..."
          data-wizard-topics-target="promptInput"
        >
        ${activeToggleMarkup}
        <button 
          type="button" 
          class="btn btn-ghost btn-xs text-error hover:bg-error/10" 
          data-action="wizard-topics#removePrompt"
          title="Remove prompt"
        >
          <span class="iconify lucide--x size-3"></span>
        </button>
      `;

      promptsContainer.appendChild(promptRow);

      // Focus the new prompt input
      const input = promptRow.querySelector(
        '[data-wizard-topics-target="promptInput"]'
      );
      if (input) input.focus();
    }
  }

  removePrompt(event) {
    event.preventDefault();
    const row = event.target.closest("[data-prompt-row]");
    if (row) {
      const promptsContainer = row.parentElement;
      row.remove();

      // If no prompts left, add one empty prompt
      if (
        promptsContainer &&
        promptsContainer.querySelectorAll("[data-prompt-row]").length === 0
      ) {
        const keywordRow = promptsContainer.closest("[data-keyword-row]");
        const addPromptButton = keywordRow.querySelector(
          '[data-action*="addPrompt"]'
        );
        if (addPromptButton) {
          this.addPrompt({ currentTarget: addPromptButton });
        }
      }
    }
  }

  togglePromptActive(event) {
    const checkbox = event.currentTarget;
    const row = checkbox.closest("[data-prompt-row]");
    const hiddenInput = row.querySelector(
      '[data-wizard-topics-target="promptActiveInput"]'
    );

    if (hiddenInput) {
      hiddenInput.value = checkbox.checked ? "1" : "0";
    }

    if (row) {
      row.classList.toggle("opacity-60", !checkbox.checked);
    }
  }

  async fetchPAA(event) {
    const button = event.currentTarget;
    const topicName = button.getAttribute("data-topic-name");
    const keywordIndex = button.getAttribute("data-keyword-index");
    const keywordRow = button.closest("[data-keyword-row]");
    const keywordInput = keywordRow.querySelector(
      '[data-wizard-topics-target="keywordInput"]'
    );
    const keyword = keywordInput.value.trim();

    if (!keyword) {
      alert("Please enter a keyword first");
      keywordInput.focus();
      return;
    }

    const targetId = `${topicName}_${keywordIndex}`;
    const domainUrl = this.element.dataset.domainUrl || "";

    // Disable button and show loading state
    button.disabled = true;
    const originalContent = button.innerHTML;
    button.innerHTML =
      '<span class="loading loading-spinner loading-xs"></span> Fetching...';

    try {
      const response = await fetch("/people_also_ask/fetch", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            .content,
        },
        body: JSON.stringify({
          keyword: keyword,
          domain_url: domainUrl,
          target_id: targetId,
        }),
      });

      if (response.ok) {
        const text = await response.text();
        // Turbo Stream will handle the update
        Turbo.renderStreamMessage(text);
        
        // After Turbo renders, add the questions to the prompts
        // Wait for DOM update
        setTimeout(() => {
          const targetId = `${topicName}_${keywordIndex}`;
          const paaResult = document.getElementById(`paa_result_${targetId}`);
          if (paaResult) {
            this.addPAAQuestionsFromTemplates(paaResult, keywordRow, topicName, keywordIndex);
          }
        }, 100);
      } else {
        const text = await response.text();
        Turbo.renderStreamMessage(text);
      }
    } catch (error) {
      console.error("PAA fetch error:", error);
      alert(`Failed to fetch questions: ${error.message}`);
    } finally {
      // Re-enable button
      button.disabled = false;
      button.disabled = false;
      button.innerHTML = originalContent;
    }
  }

  addPAAQuestionsFromTemplates(paaResult, keywordRow, topicName, keywordIndex) {
    const templates = paaResult.querySelectorAll('template[data-paa-question]');
    if (templates.length === 0) {
      console.error("No PAA question templates found");
      return;
    }
    
    const promptsContainer = keywordRow.querySelector('[data-wizard-topics-target="promptsContainer"]');
    if (!promptsContainer) {
      console.error("Could not find prompts container");
      return;
    }
    
    // Add each question as a new prompt
    templates.forEach((template) => {
      const questionText = template.content.textContent.trim();
      
      const promptRow = document.createElement("div");
      promptRow.className = "flex items-center gap-2";
      promptRow.setAttribute("data-prompt-row", "");

      // Create input element programmatically to avoid escaping issues
      const input = document.createElement("input");
      input.type = "text";
      input.name = `wizard[topics][${topicName}][keywords][${keywordIndex}][questions][]`;
      input.value = questionText;
      input.className = "input input-bordered input-sm flex-1";
      input.placeholder = "Enter a prompt or question...";
      input.setAttribute("data-wizard-topics-target", "promptInput");
      
      const removeButton = document.createElement("button");
      removeButton.type = "button";
      removeButton.className = "btn btn-ghost btn-xs text-error hover:bg-error/10";
      removeButton.setAttribute("data-action", "wizard-topics#removePrompt");
      removeButton.title = "Remove prompt";
      removeButton.innerHTML = '<span class="iconify lucide--x size-3"></span>';

      promptRow.appendChild(input);

      if (this.showActiveToggleValue) {
        const hidden = document.createElement("input");
        hidden.type = "hidden";
        hidden.name = `wizard[topics][${topicName}][keywords][${keywordIndex}][questions_active][]`;
        hidden.value = "1";
        hidden.setAttribute("data-wizard-topics-target", "promptActiveInput");

        const label = document.createElement("label");
        label.className = "flex items-center gap-1 text-xs text-base-content/60";

        const checkbox = document.createElement("input");
        checkbox.type = "checkbox";
        checkbox.className = "toggle toggle-xs";
        checkbox.checked = true;
        checkbox.setAttribute("data-action", "wizard-topics#togglePromptActive");

        const span = document.createElement("span");
        span.textContent = "Active";

        label.appendChild(checkbox);
        label.appendChild(span);

        promptRow.appendChild(hidden);
        promptRow.appendChild(label);
      }

      promptRow.appendChild(removeButton);
      promptsContainer.appendChild(promptRow);
    });
  }
}
